// ============================================================================
// AppointmentBookingDialog — запись на свободное окно.
//
// Пациент: карточка определяется по учётной записи (родитель), специалист и
// окно выбираются здесь. Врач/регистратор: дополнительно выбирает пациента из
// картотеки. Запись создаётся существующим API (CreateTableRow) напрямую на
// сервер, занятость проверяется по серверным записям; для psy — ещё и модулем.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, UserRound } from 'lucide-react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { friendlyError } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { AppointmentSlotPicker } from '@/components/medic/appointments/AppointmentSlotPicker';
import { useSlotAvailability } from '@/components/medic/appointments/useSlotAvailability';
import {
  canDo,
  CORE_DEFAULT_DURATION,
  loadPatientOptions,
  localSlotLabel,
  type AppointmentCapabilities,
  type AppointmentBookingInput,
  type AppointmentPerson,
  type AppointmentScheduleSettings,
  type AppointmentSlot,
  type AppointmentSource,
} from '@/lib/medicAppointments';

export interface AppointmentBookingDialogProps {
  open: boolean;
  mode: 'patient' | 'doctor';
  capabilities: AppointmentCapabilities;
  specialists: AppointmentPerson[];
  settings: AppointmentScheduleSettings;
  initialSource: AppointmentSource;
  initialDoctorId: string;
  initialDateKey: string;
  initialStartAt?: string;
  initialDurationMin?: number;
  /** Карточки пациента по источникам (режим пациента). */
  fixedPatients?: Partial<Record<AppointmentSource, AppointmentPerson>>;
  onClose: () => void;
  onOpenSettings: () => void;
  onSubmit: (input: AppointmentBookingInput) => Promise<void>;
}

function todayKey(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function AppointmentBookingDialog({
  open, mode, capabilities, specialists, settings, initialSource, initialDoctorId,
  initialDateKey, initialStartAt = '', initialDurationMin = 0, fixedPatients, onClose, onOpenSettings, onSubmit,
}: AppointmentBookingDialogProps) {
  const [source, setSource] = useState<AppointmentSource>(initialSource);
  const [doctorId, setDoctorId] = useState(initialDoctorId);
  const [dateKey, setDateKey] = useState(initialDateKey || todayKey());
  const [durationMin, setDurationMin] = useState(settings.durations[initialSource]);
  const [selectedStart, setSelectedStart] = useState('');
  const [comment, setComment] = useState('');
  const [patientId, setPatientId] = useState('');
  const [patients, setPatients] = useState<AppointmentPerson[]>([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientsError, setPatientsError] = useState('');
  const [patientQuery, setPatientQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const sourceSpecialists = useMemo(
    () => specialists.filter((person) => person.sources.includes(source)),
    [specialists, source],
  );

  useEffect(() => {
    if (!open) return;
    setSource(initialSource);
    setDoctorId(initialDoctorId || '');
    setDateKey(initialDateKey || todayKey());
    setDurationMin(initialSource === 'core'
      ? CORE_DEFAULT_DURATION
      : (initialDurationMin || settings.durations[initialSource]));
    setSelectedStart(initialStartAt);
    setComment('');
    setPatientId('');
    setPatientQuery('');
    setError('');
  }, [open, initialSource, initialDoctorId, initialDateKey, initialStartAt, initialDurationMin]);

  // Специалист по умолчанию — первый доступный в источнике.
  useEffect(() => {
    if (!open) return;
    const exists = sourceSpecialists.some((person) => person.id === doctorId);
    if (!exists && sourceSpecialists.length > 0) {
      setDoctorId(sourceSpecialists[0].id);
    }
    if (sourceSpecialists.length === 0) setDoctorId('');
  }, [open, sourceSpecialists, doctorId]);

  // Длительность источника — при смене источника.
  useEffect(() => {
    setDurationMin(source === 'core' ? CORE_DEFAULT_DURATION : settings.durations[source]);
  }, [source]);

  // Пациенты для записи врачом.
  useEffect(() => {
    if (!open || mode !== 'doctor') return;
    let cancelled = false;
    setPatientsLoading(true);
    setPatientsError('');
    loadPatientOptions(source, capabilities)
      .then((result) => {
        if (cancelled) return;
        setPatients(result.items);
        setPatientsError(result.error);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setPatients([]);
        setPatientsError(e instanceof Error ? e.message : 'Не удалось загрузить пациентов');
      })
      .finally(() => {
        if (!cancelled) setPatientsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, mode, source, capabilities, refreshKey]);

  // Смена источника — выбранный пациент другого источника не подходит.
  useEffect(() => {
    setPatientId('');
  }, [source]);

  const availability = useSlotAvailability({
    capabilities,
    source,
    doctorId,
    dateKey,
    durationMin,
    settings,
    refreshKey,
    enabled: open,
  });

  const selectSlot = useCallback((slot: AppointmentSlot) => {
    setSelectedStart(slot.startAt);
    setError('');
  }, []);

  const changeSource = (next: AppointmentSource) => {
    setSource(next);
    setSelectedStart('');
    setPatientId('');
    setPatientQuery('');
    setError('');
  };

  const patient = mode === 'patient'
    ? fixedPatients?.[source] || null
    : patients.find((item) => item.id === patientId) || null;

  const visiblePatients = useMemo(() => {
    const query = patientQuery.trim().toLowerCase();
    const filtered = query
      ? patients.filter((item) => item.name.toLowerCase().includes(query))
      : patients;
    const selected = patientId ? patients.find((item) => item.id === patientId) : null;
    const base = selected && !filtered.some((item) => item.id === selected.id)
      ? [selected, ...filtered]
      : filtered;
    return base.slice(0, 300);
  }, [patients, patientQuery, patientId]);

  const canCreateHere = !!capabilities[source].available && canDo(capabilities[source].rights, 'create');
  const canSubmit = canCreateHere
    && !!doctorId
    && !!selectedStart
    && !!patient?.id
    && !busy
    && availability.slots.some((slot) => slot.startAt === selectedStart && slot.available);

  const submit = async () => {
    if (!canSubmit || !patient) return;
    setBusy(true);
    setError('');
    try {
      await onSubmit({
        source,
        patientId: patient.id,
        doctorId,
        startAt: selectedStart,
        durationMin,
        comment: comment.trim(),
      });
      onClose();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const sourceLabel = source === 'core' ? 'медицинскому специалисту' : 'психологу';

  return (
    <UniversalWindow
      open={open}
      onClose={onClose}
      title="Запись на приём"
      width="w-[min(820px,96vw)]"
      height="h-[min(88vh,92vh)]"
      topBar={(
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 px-2" onClick={onClose} disabled={busy}>Отмена</Button>
          <Button size="sm" className="h-7 px-2" onClick={() => void submit()} disabled={!canSubmit || busy}>
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            Записаться
          </Button>
        </div>
      )}
    >
      <div className="h-full space-y-3 overflow-auto p-4">
        <div className="rounded-lg border bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground">
          Запись к {sourceLabel}. Свободные окна исключают уже занятое время специалиста;
          после сохранения запись появится в списке и в календаре специалиста.
        </div>

        {mode === 'doctor' && (
          <div className="grid gap-2 rounded-lg border bg-card p-3 sm:grid-cols-[minmax(0,1fr)_auto]">
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Пациент</div>
              <input
                className="mb-1 h-7 w-full rounded-md border bg-background px-2 text-[11px]"
                value={patientQuery}
                onChange={(event) => setPatientQuery(event.target.value)}
                placeholder="Поиск пациента по имени/ФИО…"
                disabled={patientsLoading}
              />
              <select
                className="h-8 w-full rounded-md border bg-background px-2 text-xs"
                value={patientId}
                onChange={(event) => setPatientId(event.target.value)}
                disabled={patientsLoading}
              >
                <option value="">
                  {patientsLoading ? 'Загрузка пациентов…' : 'Выберите пациента'}
                </option>
                {visiblePatients.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
              {visiblePatients.length === 300 && (
                <div className="mt-1 text-[10px] text-muted-foreground">Показаны первые 300 — уточните поиск.</div>
              )}
              {patientsError && <div className="mt-1 text-[10px] text-destructive">{patientsError}</div>}
              {!patientsLoading && !patientsError && patients.length === 0 && (
                <div className="mt-1 text-[10px] text-muted-foreground">
                  В картотеке нет пациентов. Создайте карточку пациента в разделе «Медицина».
                </div>
              )}
            </div>
            <div className="flex items-end">
              <Button
                size="sm"
                variant="outline"
                className="h-8 px-2 text-[11px]"
                onClick={() => setRefreshKey((value) => value + 1)}
                disabled={patientsLoading}
              >
                Обновить картотеку
              </Button>
            </div>
          </div>
        )}

        {mode === 'patient' && (
          <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs">
            <UserRound className="h-4 w-4 text-primary" />
            <span className="text-muted-foreground">Пациент:</span>
            <span className="font-medium">{fixedPatients?.[source]?.name || 'Карточка не определена'}</span>
          </div>
        )}

        <AppointmentSlotPicker
          capabilities={capabilities}
          specialists={specialists}
          source={source}
          onSourceChange={changeSource}
          specialistId={doctorId}
          onSpecialistChange={(id) => { setDoctorId(id); setSelectedStart(''); }}
          dateKey={dateKey}
          onDateChange={(key) => { setDateKey(key); setSelectedStart(''); }}
          durationMin={durationMin}
          onDurationChange={(minutes) => { setDurationMin(minutes); setSelectedStart(''); }}
          slots={availability.slots}
          closed={availability.closed}
          loading={availability.loading}
          error={availability.error}
          selectedStart={selectedStart}
          onSelectSlot={selectSlot}
          onOpenSettings={onOpenSettings}
          onRefresh={availability.reload}
          patientMode={mode === 'patient'}
          showSettings={mode === 'doctor'}
        />

        {mode === 'doctor' && source === 'psy' && (
          <label className="block space-y-1 text-xs">
            <span className="text-muted-foreground">Комментарий (необязательно)</span>
            <textarea
              className="min-h-[64px] w-full rounded-md border bg-background px-2 py-1.5 text-xs"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Например: первичный приём, подготовка, просьба пациента"
            />
          </label>
        )}

        {selectedStart && !availability.slots.some((slot) => slot.startAt === selectedStart && slot.available) && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            Выбранное окно больше недоступно. Выберите другое время.
          </div>
        )}
        {selectedStart && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
            Выбрано: <span className="font-medium">{localSlotLabel(selectedStart)}</span>
            {' · '}{durationMin} мин
            {patient ? ` · ${patient.name}` : ''}
          </div>
        )}

        {!canCreateHere && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            Роли не выдано право «Добавление» на {source === 'core' ? 'документ приёмов' : 'документ записей'}. Запись недоступна — обратитесь к администратору базы.
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
      </div>
    </UniversalWindow>
  );
}
